/* eslint-disable no-console */
/* eslint-disable react/jsx-no-bind */
import { Typography } from '@material-ui/core';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import CountyContext from '../../contexts/CountyContext';
import FiltersContext from '../../contexts/FiltersContext';
import StoryContext from '../../contexts/StoryContext';
import {
  composeWhereClause, displayChildAge, displayFacilityType, displayFamilyType,
} from '../../modules/data';
import { rdsTabulate } from '../../modules/rds';
import states, { stateFIPS } from '../../modules/states';
import { toCurrency, toPercent } from '../../modules/text';
import ResponsiveContainer from '../ResponsiveContainer';
import StateCountyMap from '../StateCountyMap';
import povertyIncome, { getRatesByState } from './data';

function CountyMapPoverty() {
  const { stateCode } = useParams();
  const {
    primaryColor, lightColor, darkColor, selectionColor, hoverColor,
  } = useContext(StoryContext);
  const {
    county, countyCode, setCounty, setCountyCode,
  } = useContext(CountyContext);

  const { familyType, settingType, childAge } = useContext(FiltersContext);

  const [data, setData] = useState([]);
  const [dataRange, setDataRange] = useState([]); // range of data for the map to calculate gradients
  const [error, setError] = useState(null);
  const [, setReady] = useState(false);

  useEffect(() => {
    function getRatesByCounty() {
      let whereClause = `state_fips=${stateFIPS[stateCode.toUpperCase()]}`;
      whereClause += composeWhereClause({ settingType, childAge });

      return rdsTabulate('ccdc', 'county_setting_group_rate_annual', {
        dims: 'county_fips',
        measure: 'average_rate:AVG(rate)',
        where: whereClause,
        format: 'amcharts',
        inject: false,
        orderby: 'county_fips',
      }, true);
    }

    // need data for state rate, county income, state income
    if (stateCode && states[stateCode.toUpperCase()]) {
      setReady(false);
      Promise.all([getRatesByCounty(), getRatesByState(stateCode, null, { settingType, childAge })])
        .then((responses) => {
          const d = [];
          const countyRates = responses[0].parsedBody.dataProvider;
          const stateRate = responses[1].parsedBody.dataProvider[0];

          const stateData = { ...stateRate, ...povertyIncome };
          const dr = []; // array of values to be used to color the map. Data range.

          countyRates.forEach((rate) => {
            const dataPoint = { ...rate, ...povertyIncome }; // merge rate and income data
            dataPoint.stateData = stateData; // add state data

            // if (dataPoint[familyType]) {
            dataPoint.value = dataPoint.average_rate / dataPoint[familyType]; // value used by map to color
            dr.push(dataPoint.value);
            // }
            d.push(dataPoint);
          });
          setData(d);
          setDataRange(dr);
        })
        .catch((err) => {
          setError(err);
        })
        .finally(() => {
          setReady(true);
        });
    }
  }, [stateCode, settingType, childAge]);

  useEffect(() => {
    // console.log(`income attribute changed to ${incomeAttribute}. Recalculating...`);
    const dr = [];
    const d = data.map((dataPoint) => {
      const dp = { ...dataPoint };
      if (dp[familyType]) {
        dp.value = dp.average_rate / dp[familyType];
        dr.push(dp.value);
      }
      return dp;
    });
    setData(d);
    setDataRange(dr);
  }, [familyType]);


  const toolTipFormatterFunction = function toolTipFormatterFunction(countyMapDatapoint, dataPoint) {
    if (!dataPoint) {
      return 'No data';
    }

    try {
      const income = dataPoint[familyType];

      const tooltipTemplate = `In <b>${countyMapDatapoint.properties.name} ${countyMapDatapoint.properties.TYPE}, ${countyMapDatapoint.properties.STATE}</b>, the average monthly price
        of full-time child care is <b>${toCurrency(dataPoint.average_rate / 12)}</b> per <b>${displayChildAge(childAge).toLowerCase()}</b> in 
        <b>${displayFacilityType(settingType).toLowerCase().replace('fcc', 'FCC')}</b>. (State price is <b>${toCurrency(dataPoint.stateData.average_rate / 12)}</b>)
        <br/><br/>
        That's <b>${toPercent(dataPoint.average_rate / income)}</b> of the median household income for
        a <b>${displayFamilyType(familyType).toLowerCase()}</b>, earning 100% FPL. (State percent price by 100% FPL is <b>${toPercent(dataPoint.stateData.average_rate / income)}</b>)
      `;

      return tooltipTemplate;
    } catch (e) {
      console.error(e);
      console.info(countyMapDatapoint);
      console.info(dataPoint);
    }
    return 'N/A';
  };

  const legendValueFormatter = function legendValueFormatter(v) {
    return Number.parseInt(v * 100, 10);
  };

  const legendLabelFormatter = function legendLabelFormatter(v) {
    return toPercent(v);
  };

  return (
    <>
      {error && <Typography variant="body1" color="error">{error.message}</Typography>}
      <ResponsiveContainer aspect={1.1} width="100%">
        <StateCountyMap
          data={data}
          dataRange={dataRange}
          legendValueFormatter={legendValueFormatter}
          legendLabelFormatter={legendLabelFormatter}
          legendTitleText={`The darker the color, the greater the percent of the median household income for a ${displayFamilyType(familyType).toLowerCase()}, earning 100% FPL, spent on ${displayFacilityType(settingType).toLowerCase().replace('fcc', 'FCC')} per ${displayChildAge(childAge).toLowerCase()}`}
          stateCode={stateCode}
          colorHigh={darkColor}
          colorLow={lightColor}
          primaryColor={primaryColor}
          selectionColor={selectionColor}
          hoverColor={hoverColor}
          setCounty={setCounty}
          setCountyCode={setCountyCode}
          county={county}
          countyCode={countyCode}
          toolTipFormatterFunction={toolTipFormatterFunction}
        />
      </ResponsiveContainer>
    </>
  );
}

export default CountyMapPoverty;
